import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';

const Generate = () => {
  const { user } = useAuth();
  const { success, error } = useNotification();
  const [products, setProducts] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [generatedKeys, setGeneratedKeys] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [productsResponse, settingsResponse] = await Promise.all([
        axios.get('/api/products'),
        axios.get('/api/settings')
      ]);
      
      setProducts(productsResponse.data.filter(p => p.isActive));
      setSettings(settingsResponse.data);
      
      // Встановити початкову кількість згідно з налаштуваннями
      const maxKeys = settingsResponse.data?.maxKeysPerGeneration || 100;
      setQuantity(Math.min(1, maxKeys));
    } catch (error) {
      console.error('Failed to fetch data:', error);
      error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleQuantityChange = (newQuantity) => {
    const maxKeys = settings?.maxKeysPerGeneration || 100;
    const validQuantity = Math.min(maxKeys, Math.max(1, parseInt(newQuantity) || 1));
    setQuantity(validQuantity);
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    
    const maxKeys = settings?.maxKeysPerGeneration || 100;
    
    if (quantity > maxKeys) {
      error(`Maximum ${maxKeys} keys per generation allowed. Please adjust the quantity.`);
      return;
    }
    
    setGenerating(true);
    
    try {
      const response = await axios.post('/api/keys/generate', {
        productId: selectedProduct,
        quantity: parseInt(quantity)
      });
      
      setGeneratedKeys(response.data.keys);
      success(`Successfully generated ${response.data.generated} license keys for FREE!\nMax allowed per generation: ${maxKeys}`, 5000);
      
      // Refresh products to get updated stats
      fetchData();
      
      // Reset form
      setSelectedProduct('');
      setQuantity(1);
    } catch (err) {
      error(err.response?.data?.message || 'Failed to generate keys');
    } finally {
      setGenerating(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    success('License key copied to clipboard!', 2000);
  };

  const copyAllKeys = () => {
    const allKeys = generatedKeys.map(k => k.key).join('\n');
    navigator.clipboard.writeText(allKeys);
    success(`Copied all ${generatedKeys.length} keys to clipboard!`, 3000);
  };

  const selectedProductData = products.find(p => p._id === selectedProduct);
  const maxKeysAllowed = settings?.maxKeysPerGeneration || 100;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  // Check if user is owner
  if (user?.role !== 'owner') {
    return (
      <div className="card-astral p-8 text-center">
        <h3 className="text-xl font-bold text-red-400 mb-2">Access Denied</h3>
        <p className="text-gray-400">
          Only owners can generate license keys. This feature is restricted to system administrators.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="card-astral p-6">
        <h1 className="text-3xl font-bold text-white mb-2">Generate License Keys</h1>
        <p className="text-gray-400">
          As an owner, you can generate license keys for any product completely free. 
          These keys can be distributed, sold, or given away as needed.
        </p>
        <div className="mt-4 p-3 bg-green-600/20 border border-green-500/50 rounded-lg">
          <div className="flex items-center text-green-400">
            <span className="text-lg mr-2">🎁</span>
            <span className="font-semibold">Owner Privilege: Free Key Generation</span>
          </div>
          <p className="text-green-300 text-sm mt-1">
            No credits will be deducted from your account for generating keys.
          </p>
        </div>
        
        {/* System Limits Info */}
        <div className="mt-4 p-3 bg-blue-600/20 border border-blue-500/50 rounded-lg">
          <div className="flex items-center text-blue-400">
            <span className="text-lg mr-2">⚙️</span>
            <span className="font-semibold">System Configuration</span>
          </div>
          <div className="text-blue-300 text-sm mt-1 space-y-1">
            <p>Maximum keys per generation: <span className="font-semibold">{maxKeysAllowed}</span></p>
            <p>You can adjust this limit in System Settings.</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Generation Form */}
        <div className="card-astral p-6">
          <h2 className="text-xl font-bold text-white mb-4">Key Generation</h2>
          
          <form onSubmit={handleGenerate} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Select Product
              </label>
              <select
                value={selectedProduct}
                onChange={(e) => setSelectedProduct(e.target.value)}
                className="w-full px-4 py-3 bg-dark-200 border border-purple-500/30 rounded-lg text-white focus:outline-none focus:border-purple-400"
                required
              >
                <option value="">Choose a product...</option>
                {products.map((product) => (
                  <option key={product._id} value={product._id}>
                    {product.name} ({product.keyTag})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Quantity (1-{maxKeysAllowed})
              </label>
              <div className="relative">
                <input
                  type="number"
                  min="1"
                  max={maxKeysAllowed}
                  value={quantity}
                  onChange={(e) => handleQuantityChange(e.target.value)}
                  className="w-full px-4 py-3 bg-dark-200 border border-purple-500/30 rounded-lg text-white focus:outline-none focus:border-purple-400"
                  required
                />
                <div className="absolute right-3 top-3 text-gray-400 text-sm">
                  keys
                </div>
              </div>
              <div className="flex justify-between items-center mt-1">
                <p className="text-xs text-gray-500">
                  Maximum {maxKeysAllowed} keys per generation (system limit)
                </p>
                {quantity > maxKeysAllowed && (
                  <p className="text-xs text-red-400">
                    Exceeds limit!
                  </p>
                )}
              </div>
              
              {/* Quick quantity buttons */}
              <div className="flex gap-2 mt-2">
                {[1, 5, 10, 25, 50, 100].filter(num => num <= maxKeysAllowed).map(num => (
                  <button
                    key={num}
                    type="button"
                    onClick={() => setQuantity(num)}
                    className={`px-3 py-1 text-xs rounded transition-colors ${
                      quantity === num 
                        ? 'bg-purple-600 text-white' 
                        : 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                    }`}
                  >
                    {num}
                  </button>
                ))}
              </div>
            </div>

            {selectedProductData && (
              <div className="bg-dark-200 p-4 rounded-lg space-y-2">
                <h4 className="text-white font-semibold mb-2">Generation Preview</h4>
                <div className="flex justify-between">
                  <span className="text-gray-400">Product:</span>
                  <span className="text-white">{selectedProductData.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Key Tag:</span>
                  <code className="text-purple-400 font-mono">{selectedProductData.keyTag}</code>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Type:</span>
                  <span className="text-white capitalize">{selectedProductData.productType}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Duration Range:</span>
                  <span className="text-white">{selectedProductData.minDays}-{selectedProductData.maxDays} days</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Pricing:</span>
                  <span className="text-white">{selectedProductData.baseCreditCost} + {selectedProductData.creditsPerDay}/day credits</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Quantity:</span>
                  <span className={`font-semibold ${quantity > maxKeysAllowed ? 'text-red-400' : 'text-white'}`}>
                    {quantity} keys
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">System Limit:</span>
                  <span className="text-blue-400">{maxKeysAllowed} keys max</span>
                </div>
                <div className="flex justify-between border-t border-gray-600 pt-2">
                  <span className="text-gray-400 font-semibold">Cost:</span>
                  <span className="text-green-400 font-semibold">FREE</span>
                </div>
                <div className="text-xs text-green-400 mt-2">
                  ✨ Owner privilege - no credits required
                </div>
                
                {quantity > maxKeysAllowed && (
                  <div className="text-xs text-red-400 mt-2 p-2 bg-red-600/20 border border-red-500/50 rounded">
                    ⚠️ Quantity exceeds system limit of {maxKeysAllowed} keys. Please reduce quantity or adjust the limit in System Settings.
                  </div>
                )}
              </div>
            )}

            <button
              type="submit"
              disabled={!selectedProduct || generating || quantity > maxKeysAllowed || quantity < 1}
              className={`w-full py-3 px-4 rounded-lg font-semibold transition-all duration-300 ${
                selectedProduct && !generating && quantity <= maxKeysAllowed && quantity >= 1
                  ? 'btn-neon' 
                  : 'bg-gray-600 text-gray-400 cursor-not-allowed'
              }`}
            >
              {generating ? (
                <div className="flex items-center justify-center">
                  <div className="spinner mr-2"></div>
                  Generating Keys...
                </div>
              ) : !selectedProduct ? (
                'Select Product to Generate'
              ) : quantity > maxKeysAllowed ? (
                `Exceeds Limit (Max: ${maxKeysAllowed})`
              ) : quantity < 1 ? (
                'Invalid Quantity'
              ) : (
                `Generate ${quantity} Key${quantity > 1 ? 's' : ''} (FREE)`
              )}
            </button>
          </form>
        </div>

        {/* Available Products & System Info */}
        <div className="space-y-6">
          {/* System Configuration */}
          <div className="card-astral p-6">
            <h2 className="text-xl font-bold text-white mb-4">System Configuration</h2>
            
            <div className="space-y-3">
              <div className="bg-dark-200 p-3 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Max Keys Per Generation:</span>
                  <span className="text-white font-semibold">{maxKeysAllowed}</span>
                </div>
              </div>
              
              <div className="bg-dark-200 p-3 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Active Products:</span>
                  <span className="text-white font-semibold">{products.length}</span>
                </div>
              </div>
              
              <div className="bg-dark-200 p-3 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Your Role:</span>
                  <span className="text-purple-400 font-semibold">OWNER</span>
                </div>
              </div>
            </div>
            
            <div className="mt-4 p-3 bg-yellow-600/20 border border-yellow-500/50 rounded-lg">
              <p className="text-yellow-400 text-sm">
                💡 You can adjust the maximum keys per generation limit in System Settings.
              </p>
            </div>
          </div>

          {/* Available Products */}
          <div className="card-astral p-6">
            <h2 className="text-xl font-bold text-white mb-4">Available Products</h2>
            
            {products.length === 0 ? (
              <p className="text-gray-400">No active products available for key generation.</p>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {products.map((product) => (
                  <div key={product._id} className="bg-dark-200 p-4 rounded-lg">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1">
                        <h4 className="font-semibold text-white">{product.name}</h4>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="px-2 py-1 bg-purple-600/20 text-purple-400 text-xs rounded font-mono">
                            {product.keyTag}
                          </span>
                          <span className="px-2 py-1 bg-blue-600/20 text-blue-400 text-xs rounded capitalize">
                            {product.productType}
                          </span>
                        </div>
                      </div>
                      <span className="text-green-400 font-semibold text-sm">
                        FREE
                      </span>
                    </div>
                    <p className="text-gray-400 text-sm">{product.description}</p>
                    
                    <div className="mt-3 p-2 bg-dark-100 rounded border-l-4 border-blue-500">
                      <div className="text-xs space-y-1">
                        <div className="flex justify-between">
                          <span className="text-gray-400">Base cost:</span>
                          <span className="text-blue-400">{product.baseCreditCost} credits</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-400">Per day:</span>
                          <span className="text-blue-400">{product.creditsPerDay} credits</span>
                        </div>
                        <div className="flex justify-between border-t border-gray-600 pt-1">
                          <span className="text-gray-400">1 day total:</span>
                          <span className="text-white font-semibold">{product.baseCreditCost + product.creditsPerDay} credits</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-400">30 days total:</span>
                          <span className="text-white font-semibold">{product.baseCreditCost + (30 * product.creditsPerDay)} credits</span>
                        </div>
                      </div>
                    </div>
                    
                    {product.stats && (
                      <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
                        <div className="text-center">
                          <div className="text-white font-semibold">{product.stats.totalGenerated || 0}</div>
                          <div className="text-gray-500">Generated</div>
                        </div>
                        <div className="text-center">
                          <div className="text-white font-semibold">{product.stats.totalSold || 0}</div>
                          <div className="text-gray-500">Sold</div>
                        </div>
                        <div className="text-center">
                          <div className="text-white font-semibold">{product.stats.revenue || 0}</div>
                          <div className="text-gray-500">Credits</div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Generated Keys */}
      {generatedKeys.length > 0 && (
        <div className="card-astral p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-white">
              Generated Keys ({generatedKeys.length})
            </h2>
            <button
              onClick={copyAllKeys}
              className="btn-neon-blue"
            >
              Copy All Keys
            </button>
          </div>
          
          <div className="bg-dark-200 p-4 rounded-lg">
            <div className="grid grid-cols-1 gap-2 max-h-64 overflow-y-auto">
              {generatedKeys.map((keyData, index) => (
                <div key={keyData._id} className="flex items-center justify-between bg-dark-100 p-3 rounded border">
                  <div className="flex items-center space-x-3">
                    <span className="text-gray-400 text-sm w-8">
                      #{index + 1}
                    </span>
                    <code className="text-purple-400 font-mono text-sm">
                      {keyData.key}
                    </code>
                  </div>
                  <button
                    onClick={() => copyToClipboard(keyData.key)}
                    className="text-gray-400 hover:text-purple-400 transition-colors"
                    title="Copy to clipboard"
                  >
                    📋
                  </button>
                </div>
              ))}
            </div>
          </div>
          
          <div className="mt-4 p-3 bg-green-600/20 border border-green-500/50 rounded-lg">
            <p className="text-green-400 text-sm">
              ✅ Successfully generated {generatedKeys.length} license keys at no cost.
              You can now distribute these keys to users or use them for testing.
              {generatedKeys.length === maxKeysAllowed && (
                <span className="block mt-1 text-yellow-400">
                  ⚠️ Maximum generation limit reached. Adjust limit in System Settings if needed.
                </span>
              )}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Generate;
